import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

export interface HeroPanelProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly date?: string;
  /** Salutation du jour — fournie par l'appelant, déjà traduite via le catalogue. */
  readonly greeting: string;
  readonly mantra?: string;
  readonly image?: string;
  /** Point focal CSS `object-position` de `image` (ex. `50% 40%`) — propre à chaque photo du pack. */
  readonly imagePosition?: string;
  readonly aside?: ReactNode;
  readonly height?: number;
  readonly compact?: boolean;
  readonly style?: CSSProperties;
}

/**
 * Bloc d'accueil. Sur desktop, deux colonnes distinctes de même hauteur côte à côte : à gauche la
 * photographie atmosphérique pleinement visible (salutation, mantra en surimpression légère via
 * `ai-veil` seul, sans panneau opaque ni pastille par-dessus) ; à droite, dans sa propre colonne
 * étirée à la même hauteur, le panneau `aside` ("Lignes directrices du jour") — jamais superposé à
 * l'image (voir revue visuelle « correction hero »). Sans `aside` ou en mode compact, une seule
 * colonne. Aucun badge Mode/État ici : ces contrôles vivent exclusivement dans l'en-tête
 * (`AppHeader`/`ContextStatus`), pour ne jamais laisser croire qu'un état est actif par défaut.
 */
export function HeroPanel({
  date,
  greeting,
  mantra,
  image,
  imagePosition = '50% 50%',
  aside,
  height = 250,
  compact = false,
  style,
  ...rest
}: HeroPanelProps) {
  const twoColumn = Boolean(aside) && !compact;

  return (
    <section
      {...rest}
      style={{
        display: 'grid',
        gridTemplateColumns: twoColumn ? 'minmax(0, 1.7fr) minmax(280px, 1fr)' : '1fr',
        gap: twoColumn ? 20 : 0,
        alignItems: 'stretch',
        ...style,
      }}
    >
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: height,
          borderRadius: 'var(--radius-panel)',
          border: '1px solid var(--accent-line)',
          boxShadow: 'var(--shadow-panel)',
          background: 'var(--surface-deep)',
        }}
      >
        {image && (
          <img
            src={image}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: imagePosition,
            }}
          />
        )}
        <div className="ai-veil" style={{ position: 'absolute', inset: 0, background: 'var(--veil-left)' }} />
        <div
          style={{
            position: 'relative',
            padding: compact ? '22px var(--gutter-mobile)' : '28px 30px',
            maxWidth: twoColumn ? 'none' : '68%',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 11,
              letterSpacing: 'var(--tracking-label)',
              textTransform: 'uppercase',
              color: 'var(--text-body)',
            }}
          >
            {date}
          </div>
          <h1
            style={{
              margin: '14px 0 18px',
              font: 'var(--type-hero)',
              fontSize: compact ? 'var(--fs-hero-sm)' : 'var(--fs-hero)',
              color: 'var(--text-display)',
            }}
          >
            {greeting}
          </h1>
          {mantra && (
            <p
              style={{
                margin: '18px 0 0',
                fontFamily: 'var(--font-display)',
                fontWeight: 300,
                fontSize: compact ? 19 : 21,
                lineHeight: 1.4,
                color: 'var(--text-display)',
                maxWidth: twoColumn ? 440 : 'none',
              }}
            >
              {mantra}
            </p>
          )}
        </div>
      </div>
      {twoColumn && aside}
    </section>
  );
}
